import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useCollectionStore } from './collectionStore';

type CollectionLike = { id: string; files: { id: string }[] };

/**
 * Helper function:
 * A collection is selected if and only if its files exactly match the selected files
 * (all collection files are selected AND no extra files exist beyond the collection).
 */
function syncCollectionsToFiles(
  newSelectedFiles: string[],
  prevSelectedCollections: string[],
  collections: CollectionLike[],
): string[] {
  const updated = prevSelectedCollections.filter((collectionId) => {
    const collection = collections.find((c) => c.id === collectionId);
    if (!collection) return false;
    const ids = collection.files.map((f) => f.id);
    return ids.length > 0 &&
      ids.every((id) => newSelectedFiles.includes(id)) &&
      newSelectedFiles.length === ids.length;
  });

  collections.forEach((collection) => {
    if (updated.includes(collection.id)) return;
    const ids = collection.files.map((f) => f.id);
    if (ids.length === 0) return;
    if (
      ids.every((id) => newSelectedFiles.includes(id)) &&
      newSelectedFiles.length === ids.length
    ) {
      updated.push(collection.id);
    }
  });

  return updated;
}

type DocumentSelectionState = {
  selectedFilesDoc: string[];
  selectedCollections: string[];

  setSelectedFiles: (updater: string[] | ((prev: string[]) => string[])) => void;
  setSelectedCollections: (
    updater: string[] | ((prev: string[]) => string[]),
  ) => void;
  addDocument: (documentId: string) => void;
  toggleCollection: (collectionId: string) => void;
  removeSelectedFiles: (ids: string[]) => void;
  removeSelectedCollectionIds: (ids: string[]) => void;
  clearAllSelections: () => void;
};

export const useDocumentSelectionStore = create<DocumentSelectionState>()(
  devtools(
    (set, get) => ({
      selectedFilesDoc: [],
      selectedCollections: [],

      setSelectedFiles: (updater) => {
        const { collections } = useCollectionStore.getState();
        set(
          (state) => {
            const newSelectedFiles =
              typeof updater === 'function'
                ? (updater as (p: string[]) => string[])(state.selectedFilesDoc)
                : updater;

            return {
              selectedFilesDoc: newSelectedFiles,
              selectedCollections: syncCollectionsToFiles(
                newSelectedFiles,
                state.selectedCollections,
                collections,
              ),
            };
          },
          false,
          'documentSelection/setSelectedFiles',
        );
      },

      setSelectedCollections: (updater) =>
        set(
          (state) => ({
            selectedCollections:
              typeof updater === 'function'
                ? (updater as (p: string[]) => string[])(state.selectedCollections)
                : updater,
          }),
          false,
          'documentSelection/setSelectedCollections',
        ),

      addDocument: (documentId: string) => {
        const { collections } = useCollectionStore.getState();

        set(
          (state) => {
            const newSelectedFiles = state.selectedFilesDoc.includes(documentId)
              ? state.selectedFilesDoc.filter((id) => id !== documentId)
              : [...state.selectedFilesDoc, documentId];

            return {
              selectedFilesDoc: newSelectedFiles,
              selectedCollections: syncCollectionsToFiles(
                newSelectedFiles,
                state.selectedCollections,
                collections,
              ),
            };
          },
          false,
          'documentSelection/addDocument(toggleFile)',
        );
      },

      toggleCollection: (collectionId: string) => {
        const { collections } = useCollectionStore.getState();
        const state = get();

        const collection = collections.find((c) => c.id === collectionId);
        if (!collection) return;

        const collectionFileIds = collection.files.map((f) => f.id);
        const isCollectionSelected = state.selectedCollections.includes(collectionId);

        if (isCollectionSelected) {
          set(
            (s) => {
              const otherSelectedCollections = s.selectedCollections.filter(
                (id) => id !== collectionId,
              );

              const filesInOtherCollections = new Set<string>();
              otherSelectedCollections.forEach((otherId) => {
                const other = collections.find((c) => c.id === otherId);
                if (other) other.files.forEach((f) => filesInOtherCollections.add(f.id));
              });

              const nextFiles = s.selectedFilesDoc.filter((fileId) => {
                const isInCurrent = collectionFileIds.includes(fileId);
                const isInOther = filesInOtherCollections.has(fileId);
                return !isInCurrent || isInOther;
              });

              return {
                selectedCollections: otherSelectedCollections,
                selectedFilesDoc: nextFiles,
              };
            },
            false,
            'documentSelection/toggleCollection(deselect)',
          );
        } else {
          set(
            (s) => {
              const newFiles = collectionFileIds.filter((id) => !s.selectedFilesDoc.includes(id));
              return {
                selectedCollections: [...s.selectedCollections, collectionId],
                selectedFilesDoc: [...s.selectedFilesDoc, ...newFiles],
              };
            },
            false,
            'documentSelection/toggleCollection(select)',
          );
        }
      },

      removeSelectedFiles: (ids) => {
        const { collections } = useCollectionStore.getState();
        set(
          (state) => {
            const newSelectedFiles = state.selectedFilesDoc.filter((id) => !ids.includes(id));

            return {
              selectedFilesDoc: newSelectedFiles,
              selectedCollections: syncCollectionsToFiles(
                newSelectedFiles,
                state.selectedCollections,
                collections,
              ),
            };
          },
          false,
          'documentSelection/removeSelectedFiles',
        );
      },

      removeSelectedCollectionIds: (ids) =>
        set(
          (s) => ({
            selectedCollections: s.selectedCollections.filter((id) => !ids.includes(id)),
          }),
          false,
          'documentSelection/removeSelectedCollectionIds',
        ),

      clearAllSelections: () =>
        set(
          { selectedFilesDoc: [], selectedCollections: [] },
          false,
          'documentSelection/clearAllSelections',
        ),
    }),
    { name: 'useDocumentSelectionStore' },
  ),
);
